<template>
  <div class="chat-view">
    <!-- Chat Header -->
    <div class="chat-header">
      <div class="chat-info">
        <div class="chat-avatar">
          {{ getChatInitials() }}
        </div>
        <div class="chat-title-wrapper">
          <h3 class="chat-title">{{ getChatName(chat) }}</h3>
          <div class="chat-details">
            <span v-if="chat.isGroup" class="group-info">
              <v-icon size="14">mdi-account-group</v-icon>
              {{ chat.users.length }} members
            </span>
            <span v-else class="user-status" :class="{ online: userStatus === 'Online' }">
              <span class="status-dot" :class="{ online: userStatus === 'Online' }"></span>
              {{ userStatus }}
            </span>
          </div>
        </div>
      </div>
      
      <div class="chat-actions">
        <button 
          @click="showSearch = !showSearch" 
          class="btn-icon" 
          :class="{ active: showSearch }"
          title="Search Messages"
        >
          <v-icon size="20">mdi-magnify</v-icon>
        </button>
        <button 
          @click="showChatSettings = true" 
          class="btn-icon" 
          title="Chat Settings"
        >
          <v-icon size="20">mdi-cog</v-icon>
        </button>
      </div>
    </div>

    <!-- Search Bar -->
    <transition name="slide-down">
      <div v-if="showSearch" class="search-bar">
        <div class="search-wrapper">
          <v-icon class="search-icon" size="18">mdi-magnify</v-icon>
          <input 
            v-model="searchQuery"
            @keyup.enter="searchMessages"
            placeholder="Search messages..."
            class="search-input"
            ref="searchInput"
          />
          <button 
            v-if="searchQuery"
            @click="clearSearch" 
            class="btn-clear-search"
            title="Clear"
          >
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </div>
        <button 
          @click="searchMessages" 
          :disabled="searchQuery.length < 3"
          class="btn-search"
        >
          <v-icon left size="18">mdi-magnify</v-icon>
          Search
        </button>
      </div>
    </transition>

    <!-- Search Results -->
    <transition name="slide-down">
      <div v-if="searchResults.length > 0" class="search-results">
        <div class="results-header">
          <v-icon left size="16">mdi-file-search</v-icon>
          <span>Found {{ searchResults.length }} message(s)</span>
          <button @click="clearSearch" class="btn-close-results">
            <v-icon size="16">mdi-close</v-icon>
          </button>
        </div>
        <div class="results-list">
          <div 
            v-for="result in searchResults" 
            :key="result._id"
            @click="scrollToMessage(result._id)"
            class="search-result-item"
          >
            <div class="result-content">{{ result.content }}</div>
            <div class="result-meta">
              <v-icon size="12">mdi-clock-outline</v-icon>
              <span>{{ formatDate(result.createdAt) }}</span>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Messages Container -->
    <div class="messages-container" ref="messagesContainer" @scroll="handleScroll">
      <!-- Load More Button -->
      <transition name="fade">
        <div v-if="hasMoreMessages && !loadingMessages" class="load-more-container">
          <button @click="$emit('load-more')" class="btn-load-more">
            <v-icon left size="16">mdi-arrow-up</v-icon>
            Load older messages
          </button>
        </div>
      </transition>

      <!-- Loading Indicator -->
      <transition name="fade">
        <div v-if="loadingMessages" class="loading-indicator">
          <div class="spinner-small"></div>
          <span>Loading messages...</span>
        </div>
      </transition>

      <!-- Messages List -->
      <div class="messages-list">
        <MessageItem
          v-for="(message, index) in groupedMessages"
          :key="message._id || `group-${index}`"
          :message="message"
          :isGrouped="message.isGrouped"
          :showAvatar="shouldShowAvatar(message, index)"
          :isGroupChat="chat.isGroup"
          :isOwn="message.senderId === getCurrentUserId"
          @react="handleReact"
          @delete="handleDelete"
          @reply="handleReply"
        />
      </div>
    </div>

    <!-- Message Input Container -->
    <div class="message-input-container">
      <!-- Reply Preview -->
      <transition name="slide-up">
        <div v-if="replyingTo" class="reply-preview">
          <div class="reply-icon">
            <v-icon size="18" color="warning">mdi-reply</v-icon>
          </div>
          <div class="reply-content">
            <span class="reply-label">Replying to:</span>
            <span class="reply-text">{{ replyingTo.content.substring(0, 100) }}{{ replyingTo.content.length > 100 ? '...' : '' }}</span>
          </div>
          <button @click="cancelReply" class="btn-cancel-reply" title="Cancel Reply">
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </div>
      </transition>

      <!-- Typing Indicator -->
      <TypingIndicator 
        v-if="typingUsers.size > 0"
        :typing-users="Array.from(typingUsers.values())"
      />

      <!-- Input Area -->
      <div class="input-area">
        <div class="input-wrapper">
          <button 
            @click="toggleEmojiPicker"
            class="btn-emoji"
            :class="{ active: showEmojiPicker }"
            title="Add Emoji"
          >
            <v-icon size="20">mdi-emoticon-happy-outline</v-icon>
          </button>
          
          <textarea
            v-model="messageText"
            @keydown="handleKeyDown"
            @input="handleTyping"
            @blur="stopTyping"
            placeholder="Type a message..."
            class="message-input"
            rows="1"
            ref="messageInput"
          ></textarea>
          
          <button 
            @click="sendMessage"
            :disabled="!messageText.trim()"
            class="btn-send"
            title="Send Message (Enter)"
          >
            <v-icon size="20">mdi-send</v-icon>
          </button>
        </div>
      </div>

      <!-- Emoji Picker -->
      <transition name="slide-up">
        <div v-if="showEmojiPicker" class="emoji-picker">
          <div class="emoji-header">
            <span class="emoji-title">Pick an emoji</span>
            <button @click="showEmojiPicker = false" class="btn-close-emoji">
              <v-icon size="16">mdi-close</v-icon>
            </button>
          </div>
          <div class="emoji-grid">
            <button 
              v-for="emoji in commonEmojis"
              :key="emoji"
              @click="insertEmoji(emoji)"
              class="emoji-item"
            >
              {{ emoji }}
            </button>
          </div>
        </div>
      </transition>
    </div>

    <!-- Chat Settings Modal -->
    <ChatSettingsModal
      v-if="showChatSettings"
      :chat="chat"
      @close="showChatSettings = false"
      @user-added="$emit('chat-updated')"
      @user-removed="$emit('chat-updated')"
      @chat-left="$emit('chat-left')"
      @chat-updated="$emit('chat-updated')"
    />
  </div>
</template>

<script>
import MessageItem from '@/views/chat/MessageItem.vue'
import TypingIndicator from '@/views/chat/TypingIndicator.vue'
import ChatSettingsModal from '@/views/chat/ChatSettingsModal.vue'
import chatService from '@/services/chat/ChatService'

export default {
  name: 'ChatView',
  components: {
    MessageItem,
    TypingIndicator,
    ChatSettingsModal
  },
  props: {
    chat: {
      type: Object,
      required: true
    },
    messages: {
      type: Array,
      default: () => []
    },
    loadingMessages: {
      type: Boolean,
      default: false
    },
    typingUsers: {
      type: Map,
      default: () => new Map()
    },
    hasMoreMessages: {
      type: Boolean,
      default: false
    }
  },
  emits: [
    'send-message',
    'load-more', 
    'react-to-message',
    'delete-message',
    'start-typing',
    'stop-typing',
    'chat-updated',
    'chat-left'
  ],
  data() {
    return {
      messageText: '',
      showSearch: false,
      showChatSettings: false,
      showEmojiPicker: false,
      searchQuery: '',
      searchResults: [],
      replyingTo: null,
      typingTimeout: null,
      isTyping: false,
      userStatus: 'Offline',
      previousMessagesLength: 0,
      commonEmojis: [
        '😀', '😂', '😍', '🤔', '😢', '😡', '👍', '👎', 
        '❤️', '💔', '🔥', '⭐', '✅', '❌', '💯', '🎉',
        '😎', '🙄', '😴', '🤗', '🤩', '😱', '🥳', '🤯'
      ]
    }
  },
  beforeUnmount() {
    this.stopTyping()
  },
  computed: {
    getCurrentUserId() {
      return this.$store.getters.getUserData.id || 'current-user-id'
    },
    groupedMessages() {
      const grouped = []
      let lastSender = null
      let lastTime = null
      
      this.messages.forEach((message, index) => {
        const messageTime = new Date(message.createdAt)
        const timeDiff = lastTime ? messageTime - lastTime : Infinity
        // Group messages from same sender within 5 minutes
        const shouldGroup = 
          lastSender === message.senderId && 
          timeDiff < 5 * 60 * 1000
        
        grouped.push({
          ...message,
          isGrouped: shouldGroup
        })
        
        lastSender = message.senderId
        lastTime = messageTime
      })
      
      return grouped
    }
  },

  watch: {
    groupedMessages: {
      handler(newMessages, oldMessages) {
        // Scroll only if new messages are added at the bottom (not from load-more)
        if (
          newMessages.length > this.previousMessagesLength &&
          !this.loadingMessages // don't scroll if loading more messages (load-more)
        ) {
          const container = this.$refs.messagesContainer
          if (container) {
            // Check if user is near the top (for load-more)
            // If previousMessagesLength was 0 (fresh chat), always scroll to bottom
            if (this.previousMessagesLength === 0) {
              this.scrollToBottom()
            } else {
              const isNearTop = container.scrollTop < 100
              if (!isNearTop) {
                this.scrollToBottom()
              }
            }
          } else {
            this.scrollToBottom()
          }
        }
        this.previousMessagesLength = newMessages.length
      },
      immediate: false
    },

    'chat._id': {
      handler(newId, oldId) {
        if (newId !== oldId) {
          // Nowy chat - force scroll
          this.$nextTick(() => {
            this.scrollToBottom(true)
            this.focusInput()
          })
        }
      }
    },

    showSearch(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.$refs.searchInput?.focus()
        })
      }
    }
  },

  mounted() {
    this.scrollToBottom(true)
    this.focusInput()
    this.previousMessagesLength = this.messages.length
    this.getUserStatus(this.getOtherUserId()).then(status => {
      this.userStatus = status
    })
  },
  
  updated() {
    this.getUserStatus(this.getOtherUserId()).then(status => {
      this.userStatus = status
    })
  },
  
  methods: {
    getChatName(chat) {
      if (chat.isGroup) {
        return chat.groupName || `Group (${chat.users.length} members)`
      }
      const otherUser = chat.users.find(user => user[0] !== this.getCurrentUserId)
      return otherUser ? otherUser[1] : 'Unknown User'
    },

    getChatInitials() {
      const name = this.getChatName(this.chat)
      return name.substring(0, 2).toUpperCase()
    },

    getOtherUserId() {
      if (this.chat.isGroup) return null
      const usx = this.chat.users.find(user => user[0] !== this.getCurrentUserId)

      if (usx && usx.length > 0) {
        return usx[0]
      } else {
        return null
      }
    },

    async getUserStatus(userId) {
      try {
        if (!userId) return 'Offline'
        const response = await chatService.getUserStatusCache(userId)
        return response ? 'Online' : 'Offline'
      } catch (error) {
        return 'Offline'
      }
    },
    
    shouldShowAvatar(message, index) {
      if (index === 0) return true
      if (message.isGrouped) return false
      return true
    },

    handleKeyDown(event) {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault()
        this.sendMessage()
      }
    },

    handleTyping() {
      if (!this.isTyping) {
        this.isTyping = true
        this.$emit('start-typing')
      }

      clearTimeout(this.typingTimeout)
      this.typingTimeout = setTimeout(() => {
        this.stopTyping()
      }, 2000)

      this.autoResizeTextarea()
    },

    stopTyping() {
      if (this.isTyping) {
        this.isTyping = false
        this.$emit('stop-typing')
      }
      clearTimeout(this.typingTimeout)
    },

    autoResizeTextarea() {
      const textarea = this.$refs.messageInput
      if (textarea) {
        textarea.style.height = 'auto'
        textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px'
      }
    },

    sendMessage() {
      const text = this.messageText.trim()
      if (!text) return

      if (this.replyingTo){
        this.$emit('send-message', {msg: text, replyContent: this.replyingTo, replyTo: true})
      } else {
        this.$emit('send-message', {msg: text, replyContent: '', replyTo: false})
      }
      
      this.messageText = ''
      this.replyingTo = null
      this.stopTyping()
      this.autoResizeTextarea()
      this.focusInput()
    },

    handleReact(messageId, reaction) {
      this.$emit('react-to-message', messageId, reaction)
    },

    handleDelete(messageId) {
      this.$emit('delete-message', messageId)
    },

    handleReply(message) {
      this.replyingTo = message
      this.focusInput()
    },

    cancelReply() {
      this.replyingTo = null
    },

    async searchMessages() {
      if (!this.searchQuery.trim()) return

      if (this.searchQuery.length < 3) {
        chatService.errorChat()
        return
      }

      try {
        this.searchResults = await chatService.searchMessages(
          this.chat._id, 
          this.searchQuery
        )
      } catch (error) {
        console.error('Search error:', error)
      }
    },

    clearSearch() {
      this.searchQuery = ''
      this.searchResults = []
      this.showSearch = false
    },

    scrollToMessage(messageId) {
      const element = document.querySelector(`[data-message-id="${messageId}"]`)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' })
        element.classList.add('highlight')
        setTimeout(() => {
          element.classList.remove('highlight')
        }, 2000)
      }
    },

    toggleEmojiPicker() {
      this.showEmojiPicker = !this.showEmojiPicker
    },

    insertEmoji(emoji) {
      const textarea = this.$refs.messageInput
      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      
      this.messageText = 
        this.messageText.substring(0, start) + 
        emoji + 
        this.messageText.substring(end)
      
      this.$nextTick(() => {
        textarea.selectionStart = textarea.selectionEnd = start + emoji.length
        textarea.focus()
      })
      
      this.showEmojiPicker = false
    },

    handleScroll(event) {
      const container = event.target
      
      if (container.scrollTop === 0 && this.hasMoreMessages && !this.loadingMessages) {
        this.$emit('load-more')
      }
    },

    scrollToBottom(force = true) {
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer
        if (container) {
          const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100
          if (force || isNearBottom) {
            container.scrollTop = container.scrollHeight + 10
          }
        }
      })
    },

    focusInput() {
      this.$nextTick(() => {
        this.$refs.messageInput?.focus()
      })
    },

    formatDate(date) {
      return new Date(date).toLocaleString()
    }
  }
}
</script>

<style lang="scss" scoped>
@use '../../views/css/styles.scss';

.chat-view {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: linear-gradient(145deg, rgba(18, 18, 18, 1), rgba(10, 10, 10, 1));
  position: relative;
  border-radius: 0 10px 10px 0!important;
}

/* Chat Header */
.chat-header {
  padding: 20px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(10px);
  position: sticky;
  top: 0;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  border-radius: 0 10px 0 0!important;
}

.chat-info {
  display: flex;
  align-items: center;
  gap: 14px;
  flex: 1;
  min-width: 0;
}

.chat-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #007bff, #0056b3);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 18px;
  font-weight: 700;
  font-family: "Darker Grotesque", sans-serif;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.4);
}

.chat-title-wrapper {
  flex: 1;
  min-width: 0;
}

.chat-title {
  margin: 0 0 4px 0;
  font-family: "Darker Grotesque", sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: white;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chat-details {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
  display: flex;
  align-items: center;
  gap: 6px;
}

.group-info {
  display: flex;
  align-items: center;
  gap: 4px;
}

.user-status {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #dc3545;
  transition: color 0.3s ease;
}

.user-status.online {
  color: #28a745;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #dc3545;
  animation: pulse 2s ease-in-out infinite;
}

.status-dot.online {
  background: #28a745;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.chat-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.btn-icon {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  color: white;
}

.btn-icon:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.4);
  transform: translateY(-2px);
}

.btn-icon.active {
  background: rgba(0, 123, 255, 0.2);
  border-color: #007bff;
  color: #007bff;
}

/* Search Bar */
.search-bar {
  padding: 16px 24px;
  background: rgba(0, 0, 0, 0.3);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  gap: 12px;
  align-items: center;
}

.search-wrapper {
  position: relative;
  flex: 1;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.5);
  pointer-events: none;
}

.search-input {
  width: 100%;
  color: white;
  padding: 10px 16px 10px 40px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  font-size: 15px;
  font-family: "Darker Grotesque", sans-serif;
  outline: none;
  transition: all 0.3s ease;
}

.search-input::placeholder {
  color: rgba(255, 255, 255, 0.4);
}

.search-input:focus {
  border-color: rgba(255, 255, 255, 0.4);
  background: rgba(255, 255, 255, 0.08);
}

.btn-clear-search {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.btn-clear-search:hover {
  background: rgba(255, 255, 255, 0.1);
  color: white;
}

.btn-search {
  background: linear-gradient(135deg, #007bff, #0056b3);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 14px;
  font-family: "Darker Grotesque", sans-serif;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
}

.btn-search:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 123, 255, 0.4);
}

.btn-search:disabled {
  background: linear-gradient(135deg, #6c757d, #545b62);
  cursor: not-allowed;
  opacity: 0.6;
  box-shadow: none;
}

/* Search Results */
.search-results {
  background: rgba(0, 0, 0, 0.4);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  max-height: 250px;
  overflow: hidden;

  display: flex;
  flex-direction: column;
}

.results-header {
  padding: 12px 24px;
  background: rgba(0, 0, 0, 0.3);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  font-family: "Darker Grotesque", sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-close-results {
  margin-left: auto;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.btn-close-results:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.4);
}

.results-list {
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: none; /* Firefox - ukrywa scrollbar */
  -ms-overflow-style: none; /* IE/Edge - ukrywa scrollbar */
}

.results-list::-webkit-scrollbar {
  display: none;
  width: 0;
  height: 0;
  background: transparent;
}

.results-list::-webkit-scrollbar-thumb {
  display: none;
  background: transparent;
}

.search-result-item {
  padding: 12px 24px;
  background: rgba(255, 255, 255, 0.03);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  cursor: pointer;
  transition: all 0.2s ease;
}

.search-result-item:hover {
  background: rgba(0, 123, 255, 0.15);
  transform: translateX(4px);
}

.result-content {
  font-size: 14px;
  font-family: "Darker Grotesque", sans-serif;
  color: white;
  margin-bottom: 6px;
  line-height: 1.5;
  word-break: break-word;
}

.result-meta {
  font-size: 12px;
  font-family: "Darker Grotesque", sans-serif;
  color: rgba(255, 255, 255, 0.5);
  display: flex;
  align-items: center;
  gap: 4px;
}

/* Messages Container */
.messages-container {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0;
  display: flex;
  flex-direction: column;
  scrollbar-width: none; /* Firefox - ukrywa scrollbar */
  -ms-overflow-style: none; /* IE/Edge - ukrywa scrollbar */
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
  overscroll-behavior: contain;
  outline: none;
}

.messages-container::-webkit-scrollbar {
  display: none;
  width: 0;
  height: 0;
  background: transparent;
}

.messages-container::-webkit-scrollbar-track {
  display: none;
  background: transparent;
}

.messages-container::-webkit-scrollbar-thumb {
  display: none;
  background: transparent;
}

.messages-container::-webkit-scrollbar-thumb:hover {
  display: none;
  background: transparent;
}

.load-more-container {
  padding: 16px;
  text-align: center;
}

.btn-load-more {
  background: rgba(255, 255, 255, 0.05);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 8px 16px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 14px;
  font-family: "Darker Grotesque", sans-serif;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
}

.btn-load-more:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.4);
  transform: translateY(-2px);
}

.loading-indicator {
  padding: 16px;
  text-align: center;
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  font-family: "Darker Grotesque", sans-serif;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.spinner-small {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.messages-list {
  flex: 1;
  padding: 10px 0;
}

/* Message Input Container */
.message-input-container {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(10px);
  position: relative;
  border-radius: 0 0 10px 0px!important;
}

/* Reply Preview */
.reply-preview {
  padding: 12px 24px;
  background: rgba(255, 193, 7, 0.1);
  border-bottom: 1px solid rgba(255, 193, 7, 0.3);
  border-left: 3px solid #ffc107;
  display: flex;
  align-items: center;
  gap: 12px;
}

.reply-icon {
  flex-shrink: 0;
}

.reply-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.reply-label {
  font-weight: 600;
  color: #ffc107;
  font-size: 13px;
  font-family: "Darker Grotesque", sans-serif;
}

.reply-text {
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  font-family: "Darker Grotesque", sans-serif;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.btn-cancel-reply {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.btn-cancel-reply:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.4);
}

/* Input Area */
.input-area {
  padding: 16px 24px;
}

.input-wrapper {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 8px 12px;
  transition: all 0.3s ease;
}

.input-wrapper:focus-within {
  border-color: rgba(255, 255, 255, 0.4);
  background: rgba(255, 255, 255, 0.08);
}

.btn-emoji {
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.btn-emoji:hover {
  background: rgba(255, 255, 255, 0.1);
  color: white;
}

.btn-emoji.active {
  background: rgba(0, 123, 255, 0.2);
  color: #007bff;
}

.message-input {
  flex: 1;
  border: none;
  background: transparent;
  padding: 6px 8px;
  font-size: 15px;
  font-family: "Darker Grotesque", sans-serif;
  outline: none;
  resize: none;
  min-height: 24px;
  max-height: 120px;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
  color: white;
}

.message-input::placeholder {
  color: rgba(255, 255, 255, 0.4);
}

.message-input::-webkit-scrollbar {
  width: 6px;
}

.message-input::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
}

.btn-send {
  background: linear-gradient(135deg, #007bff, #0056b3);
  color: white;
  border: none;
  border-radius: 8px;
  width: 36px;
  height: 36px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
}

.btn-send:hover:not(:disabled) {
  transform: translateY(-2px) scale(1.05);
  box-shadow: 0 6px 16px rgba(0, 123, 255, 0.4);
}

.btn-send:disabled {
  background: linear-gradient(135deg, #6c757d, #545b62);
  cursor: not-allowed;
  opacity: 0.5;
  box-shadow: none;
}

/* Emoji Picker */
.emoji-picker {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 24px;
  right: 24px;
  max-width: 400px;
  background: linear-gradient(145deg, rgba(26, 26, 26, 0.98), rgba(18, 18, 18, 0.98));
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
  z-index: 100;
  backdrop-filter: blur(10px);
}

.emoji-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.emoji-title {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: white;
}

.btn-close-emoji {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.btn-close-emoji:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.4);
}

.emoji-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(40px, 1fr));
  gap: 4px;
  max-height: 200px;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: none; /* Firefox - ukrywa scrollbar */
  -ms-overflow-style: none; /* IE/Edge - ukrywa scrollbar */
  -webkit-overflow-scrolling: touch;
}

.emoji-grid::-webkit-scrollbar {
  display: none;
  width: 0;
  height: 0;
  background: transparent;
}

.emoji-grid::-webkit-scrollbar-thumb {
  display: none;
  background: transparent;
}

.emoji-item {
  font-size: 24px;
  cursor: pointer;
  padding: 8px;
  border: none;
  background: transparent;
  border-radius: 6px;
  text-align: center;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.emoji-item:hover {
  background: rgba(255, 255, 255, 0.1);
  transform: scale(1.2);
}

/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Transition Classes */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
}

.slide-down-enter-from,
.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Message Highlight */
:global(.highlight) {
  background: rgba(0, 123, 255, 0.2) !important;
  animation: highlightFade 2s ease-out;
}

@keyframes highlightFade {
  0% { 
    background: rgba(0, 123, 255, 0.3);
  }
  100% { 
    background: transparent;
  }
}

@media (max-width: 1279px) and (min-width: 769px) {
  .chat-view {
    border-radius: 10px !important;
  }

  .chat-header {
    padding: 18px 22px;
    border-radius: 10px 10px 0 0 !important;
  }

  .chat-avatar {
    width: 44px;
    height: 44px;
    font-size: 17px;
  }

  .chat-title {
    font-size: 19px;
  }

  .search-bar {
    padding: 14px 22px;
  }

  .input-area {
    padding: 14px 22px;
  }

  .message-input-container {
    border-radius: 0 0 10px 10px !important;
  }

  .emoji-picker {
    left: 22px;
    right: 22px;
  }
}

/* Responsive */
@media (max-width: 768px) {
  .chat-header {
    padding: 16px 20px;
    border-radius: 10px 10px 0 0 !important; /* Mobile - oba górne rogi */
  }

  .chat-avatar {
    width: 40px;
    height: 40px;
    font-size: 16px;
  }

  .chat-title {
    font-size: 18px;
  }

  .search-bar {
    padding: 12px 20px;
  }

  .input-area {
    padding: 12px 20px;
  }

  /* Mobile - border-radius na obu dolnych rogach */
  .message-input-container {
    border-radius: 0 0 10px 10px !important;
  }

  .emoji-picker {
    left: 20px;
    right: 20px;
  }

  .emoji-grid {
    grid-template-columns: repeat(auto-fill, minmax(36px, 1fr));
  }

  .emoji-item {
    font-size: 20px;
    padding: 6px;
  }
}

/* Dodatkowy breakpoint dla małych ekranów */
@media (max-width: 480px) {
  .chat-view {
    border-radius: 10px !important;
  }

  .chat-header {
    border-radius: 10px 10px 0 0 !important;
  }

  .message-input-container {
    border-radius: 0 0 10px 10px !important;
  }
}

/* Vuetify Overrides */
:deep(.v-icon) {
  color: inherit !important;
}
</style>